import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { fontSize, tabletMinWidth } from '../../mixins'
import UIButton from '../../UI/UIButton'
import MainContainer from '../../UI/MainContainer'
import { InvertedLogo } from '../../UI/Logo'
import { useDispatch, useSelector } from 'react-redux'
import { selectPlan, selectShowCheckout, setDefaultPlanId, setPlan, setShowCheckout } from './plansSlice'
import { PLANS } from '../../analytics/eCommerce'
import { selectEventsData, setEventDataList } from '../events/eventsSlice'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { selectStripeAccountName } from '../signup/signupSlice'

export const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  width: 100%;
  max-width: 327px;
  margin-bottom: 80px;
  position: relative;
  @media screen and (max-width: 300px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`

export const StyledItem = styled.li`
  width: 76px;
  height: 76px;
  background-color: ${(props) => (props.active ? '#3393EC' : '#ffffff')};
  border: ${(props) =>
    props.active ? '1px solid #ffffff' : '1px solid #3393EC'};
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.active ? '#ffffff' : '#323232')};
  ${fontSize(16)};
  line-height: 19px;
  cursor: pointer;
  margin-right: 8px;
  :last-child {
    margin-right: 0;
    ::after {
      content: ${(props) => (props.active ? `attr(data-text)` : '')};
      position: absolute;
      width: 80px;
      bottom: -30px;
      font-weight: 510;
      ${fontSize(12)};
      line-height: 14px;
      text-align: center;
      color: #323232;
    }
  }
  @media screen and (max-width: 300px) {
    :last-child {
      margin-right: 8px;
    }
    :nth-child(1),
    :nth-child(2) {
      margin-bottom: 8px;
    }
  }
`

const Title = styled.h1`
  font-weight: 700;
  ${fontSize(24)};
  line-height: 29px;
  text-align: center;
  color: #323232;
  margin: 20px 0 16px 0;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin: 10px 0 12px 0;
  }
  @media screen and (max-height: 750px) {
    margin: 10px 0 10px 0;
  }
`

const Description = styled.p`
  max-width: 330px;
  font-weight: 400;
  ${fontSize(16)};
  line-height: 19px;
  text-align: center;
  color: #323232;
  margin-bottom: 24px;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin-bottom: 10px;
  }
  @media screen and (max-height: 750px) {
    margin: 0 0 10px 0;
  }
`

const Cost = styled.div`
  max-width: 330px;
  background: #e9f4ff;
  font-weight: 500;
  padding: 16px;
  ${fontSize(16)};
  line-height: 19px;
  text-align: center;
  border-radius: 12px;
  color: #323232;
  margin-bottom: 32px;
  > span {
    font-weight: 800;
  }
  @media screen and (max-height: 750px) {
    margin-bottom: 15px;
  }
`

const TrialPlan = () => {
  const { t } = useTranslation()
  const plan = useSelector(selectPlan)
  const dispatch = useDispatch()
  const eventsData = useSelector(selectEventsData)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const showCheckout = useSelector(selectShowCheckout)
  const TRIAL_PLANS = useMemo(() => {
    return [
      {
        id: 'fl-monthly-intro7d-10-1996',
        price: '$1',
      },
      {
        id: 'fl-monthly-intro7d-50-1996',
        price: '$5',
      },
      {
        id: 'fl-monthly-intro7d-100-1996',
        price: '$10',
      },
      {
        id: 'fl-monthly-intro7d-1651-1996',
        price: '$16.51',
      },
    ]
  }, [])
  useEffect(() => {
    if (!showCheckout) {
      dispatch(setPlan(TRIAL_PLANS[3].id))
      dispatch(setDefaultPlanId(TRIAL_PLANS[3].id))
    }
  }, [dispatch, TRIAL_PLANS, showCheckout])

  const onPlanClick = (id) => dispatch(setPlan(id))

  const renderItem = (i, index) => {
    let isActive = plan === i.id
    return (
      <StyledItem
        onClick={() => onPlanClick(i.id)}
        data-text={t('most_popular_choise')}
        active={isActive}
        key={index}
      >
        <span>{t(i.price)}</span>
      </StyledItem>
    )
  }

  const onContinueClick = () => {
    const { productId, duration, description, periodPrice, price } = PLANS[plan]
    const planData = {
      Value: price,
      value: Math.round(periodPrice * 100),
      currency: 'USD',
      price: periodPrice,
      Duration: duration,
      duration,
      description,
      periodPrice,
      product_id: productId,
      stripe_account_name: stripeAccountName,
    }
    dispatch(setEventDataList(planData))
    sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
      ...eventsData,
      ...planData,
    })
    dispatch(setShowCheckout(true))
  }

  return (
    <MainContainer
      desktopWidth={462}
      adaptiveHeight={800}
      customPadding={'25px'}
      customTabletJustify={'center'}
    >
      <InvertedLogo width={82} height={82} />
      <Title>{`${t('pay_what_you_want')}`}</Title>
      <Description>{t('pay_what_you_want_description')}</Description>
      <Cost>{t('cost_us')} <span>{TRIAL_PLANS[3].price}</span> {t('cost_us_continue')}</Cost>
      <StyledList>{TRIAL_PLANS.map(renderItem)}</StyledList>
      <UIButton onClick={onContinueClick} position={'sticky'} bottomPosition={'24px'} customStickyWidth={'100%'}>
        {t('continue')}
      </UIButton>
    </MainContainer>
  )
}

export default TrialPlan
